import { Line } from '@antv/g2plot'
import dayjs from 'dayjs'
import { useEffect, useMemo, useRef } from 'react'

import { Trans } from '@/components'
import { useThemeMode } from '@/contexts/ThemeContext'
import { getChartThemeColors, getLineChartAxisTheme } from '@/utils/chart-theme'

import { GpuDeviceData, HostMonitorValue } from '../hooks/useNodeMonitorData'

// Color palette for multiple lines
const LINE_COLORS = ['#365FEF', '#FC9C28', '#10B981', '#8B5CF6', '#EC4899', '#F59E0B']

export interface MultiLineChartProps {
  data: GpuDeviceData[]
  selectedItems: string[]
  filterDuration: number
}

interface MultiLineValue extends HostMonitorValue {
  series: string
}

// Render tooltip section for GPU data
function renderGpuTooltipSection(
  label: string,
  markerColor: string,
  hasAggregation: boolean,
  datum: MultiLineValue,
): string {
  if (hasAggregation) {
    return `
      <div style="background: rgba(255,255,255,0.08); border-radius: 8px; padding: 4px 8px; display: flex; gap: 16px; align-items: center;">
        <div style="display: flex; gap: 8px; align-items: center; flex-shrink: 0;">
          <div style="width: 6px; height: 6px; border-radius: 50%; background: ${markerColor}; border: 2px solid ${markerColor};"></div>
          <span style="font-size: 14px; font-weight: 700; color: #ffffff;">${label}</span>
        </div>
        <div style="display: grid; grid-template-columns: repeat(2, auto); gap: 8px;">
          <div style="display: flex; gap: 16px;">
            <span style="font-size: 12px; font-weight: 600; color: rgba(255,255,255,0.65);">Avg</span>
            <span style="font-size: 12px; font-weight: 700; color: #ffffff;">${datum.avg}${datum.unit}</span>
          </div>
          <div style="display: flex; gap: 16px;">
            <span style="font-size: 12px; font-weight: 600; color: rgba(255,255,255,0.65);">Max</span>
            <span style="font-size: 12px; font-weight: 700; color: #ffffff;">${datum.max}${datum.unit}</span>
          </div>
          <div style="display: flex; gap: 16px;">
            <span style="font-size: 12px; font-weight: 600; color: rgba(255,255,255,0.65);">Min</span>
            <span style="font-size: 12px; font-weight: 700; color: #ffffff;">${datum.min}${datum.unit}</span>
          </div>
          <div style="display: flex; gap: 16px;">
            <span style="font-size: 12px; font-weight: 600; color: rgba(255,255,255,0.65);">Last</span>
            <span style="font-size: 12px; font-weight: 700; color: #ffffff;">${datum.last}${datum.unit}</span>
          </div>
        </div>
      </div>`
  } else {
    return `
      <div style="border-radius: 8px; display: flex; gap: 16px; align-items: center;">
        <div style="font-size: 12px; font-weight: 600; color: #ffffff;">${label}</div>
        <div style="font-size: 12px; font-weight: 600; color: #ffffff;">${datum.value || 0}${datum.unit}</div>
      </div>`
  }
}

export function MultiLineChart({ data, selectedItems, filterDuration }: MultiLineChartProps) {
  const { themeMode } = useThemeMode()
  const id = useMemo(() => Math.random().toString(36).replace('0.', 'ID_'), [])
  const lineRef = useRef<Line>()

  // Filter selected items
  const selectedData = useMemo(
    () => data.filter((i) => selectedItems.includes(i.name)),
    [data, selectedItems],
  )

  const isEmpty = selectedData.length === 0 || selectedData.every((i) => i.isEmpty)

  useEffect(() => {
    // Build chart data with series field
    const chartData: MultiLineValue[] = []

    selectedData.forEach((item) => {
      item.values.forEach((v) => {
        const gpuLabel = `GPU ${String(Number(item.name) + 1).padStart(2, '0')}`
        chartData.push({ ...v, series: gpuLabel })
      })
    })

    const unit = chartData[0]?.unit || '%'

    if (chartData.length > 0) {
      const chartTheme = getChartThemeColors()
      const axisTheme = getLineChartAxisTheme(themeMode, isEmpty, {
        gridColor: chartTheme.grid,
      })

      if (lineRef.current) {
        lineRef.current.destroy()
        lineRef.current = undefined
      }

      lineRef.current = new Line(id, {
        data: chartData,
        padding: 'auto',
        xField: 'time',
        yField: 'value',
        seriesField: 'series',
        color: ({ series }) => {
          if (!series) return LINE_COLORS[0]
          // Extract GPU index from "GPU 01" format
          const match = (series as string).match(/GPU (\d+)/)
          const gpuIndex = match ? String(Number(match[1]) - 1) : '0'
          const index = selectedItems.indexOf(gpuIndex)
          return LINE_COLORS[Math.max(0, index) % LINE_COLORS.length]
        },
        xAxis: {
          tickCount: 7,
          label: {
            formatter: (v) => `${dayjs(v).format('HH:mm')}`,
          },
          tickLine: { length: -5 },
          line: axisTheme.axisLine,
          grid: axisTheme.xGrid,
        },
        yAxis: {
          tickCount: 6,
          min: 0,
          max: 100,
          label: {
            formatter: (v) => `${v}${unit}`,
          },
          grid: axisTheme.yGrid,
        },
        tooltip: {
          showMarkers: false,
          customContent(title, rawData) {
            if (!rawData || rawData.length === 0) return ''

            const formattedTitle = dayjs(title).format('YYYY-MM-DD HH:mm:ss')

            let content = `
              <div style="display: flex; flex-direction: column; gap: 8px;">
                <div style="display: flex; justify-content: space-between; align-items: center; gap: 16px;">
                  <span style="font-size: 12px; font-weight: 600; color: #ffffff;">${formattedTitle}</span>
                </div>`

            rawData.forEach((item) => {
              const datum = item.data as MultiLineValue
              if (!datum?.series) return

              const hasAggregation = datum.avg !== undefined
              // Get color from LINE_COLORS based on GPU index
              const match = datum.series.match(/GPU (\d+)/)
              const gpuIndex = match ? Number(match[1]) - 1 : 0
              const colorIndex = selectedItems.indexOf(String(gpuIndex))
              const markerColor = LINE_COLORS[Math.max(0, colorIndex) % LINE_COLORS.length]

              content += renderGpuTooltipSection(datum.series, markerColor, hasAggregation, datum)
            })

            content += `</div>`
            return content
          },
          domStyles: {
            'g2-tooltip': {
              backgroundColor: '#0e121b',
              color: 'white',
              borderRadius: '8px',
              padding: '8px',
              fontSize: '12px',
              lineHeight: '16px',
              fontWeight: '500',
              textAlign: 'left',
              maxHeight: '300px',
              overflowY: 'auto',
              boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            },
          },
          showCrosshairs: true,
          crosshairs: {
            line: {
              style: { lineWidth: 1, lineDash: [5, 3], color: chartTheme.crosshair },
            },
          },
        },
        smooth: true,
        lineStyle: { lineWidth: 1 },
        legend: false,
      })
      lineRef.current.render()
    }

    return () => {
      if (lineRef.current) {
        lineRef.current.destroy()
        lineRef.current = undefined
      }
    }
  }, [id, selectedData, selectedItems, isEmpty, filterDuration, themeMode])

  return (
    <div className="h-[276px] relative">
      <div
        id={id}
        className="h-full w-full overflow-hidden mt-4 text-center text-tertiary t-sm absolute"
      />
      {isEmpty && (
        <div className="absolute h-full w-full flex items-center justify-center">
          <Trans ns="privateRegion" i18nKey="common.noData">
            No Data
          </Trans>
        </div>
      )}
    </div>
  )
}
